import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState } from "react";
import firebaseApp from "../../firebase/credenciales";
import { getFirestore, collection, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { FaSearch, FaCalendarCheck, FaCalendarTimes, FaFilePdf, FaUser, FaBalanceScale, FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";

import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

function VacacionesDetalle({ usuario }) {
  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [gozadas, setGozadas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState("");

  // Estados para Edición
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(""); // "gozadas" o "pendientes"
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const handleBuscar = async () => {
    if (!dni.trim()) return;

    setLoading(true);
    setGozadas([]);
    setPendientes([]);
    setNombre("");
    setError("");

    try {
      const refGozadas = collection(db, `vacaciones/${dni}/gozadas`);
      const refPendientes = collection(db, `vacaciones/${dni}/pendientes`);

      const [snapGozadas, snapPendientes] = await Promise.all([
        getDocs(refGozadas),
        getDocs(refPendientes),
      ]);

      const dataGozadas = snapGozadas.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const dataPendientes = snapPendientes.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setGozadas(dataGozadas);
      setPendientes(dataPendientes);

      if (dataGozadas.length > 0) {
        setNombre(dataGozadas[0].nombreCompleto);
      } else if (dataPendientes.length > 0) {
        setNombre(dataPendientes[0].nombreCompleto);
      } else {
        setError("No se encontraron registros para este DNI");
      }
    } catch (err) {
      console.error("Error al buscar datos:", err);
      setError("Error al buscar la información. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm("¿Está seguro de que desea eliminar este registro? Esta acción no se puede deshacer.")) {
      return;
    }

    try {
      setLoading(true);
      const collectionName = type === "gozadas" ? "gozadas" : "pendientes";
      const docRef = doc(db, `vacaciones/${dni}/${collectionName}`, id);

      await deleteDoc(docRef);

      // AUDITORÍA
      try {
        await logBusinessAction("ELIMINACION_AUSENCIA", "GestionAusencias", {
          dniEditado: dni,
          cambiosRealizados: {
            ausencia: { anterior: type === "gozadas" ? "Vacaciones Gozadas" : "Vacaciones Pendientes", nuevo: "Eliminada" }
          },
          idDocumento: id
        }, usuario);
      } catch (e) {
        console.warn("Error al registrar auditoría:", e);
      }

      // Refrescar datos
      handleBuscar();
    } catch (err) {
      console.error("Error al eliminar:", err);
      setError("Error al eliminar el registro.");
      setLoading(false);
    }
  };

  const handleEditClick = (item, type) => {
    setEditingItem(item);
    setEditingType(type);
    setEditForm({ ...item });
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => {
      const newData = { ...prev, [name]: value };

      // Auto-cálculo para Gozadas
      if (editingType === "gozadas" && (name === "fechaInicio" || name === "fechaFin")) {
        const strInicio = formatDateForInput(newData.fechaInicio);
        const strFin = formatDateForInput(newData.fechaFin);

        if (strInicio && strFin) {
          // Parse directo de YYYY-MM-DD
          const [y1, m1, d1] = strInicio.split('-').map(Number);
          const date1 = new Date(y1, m1 - 1, d1);

          const [y2, m2, d2] = strFin.split('-').map(Number);
          const date2 = new Date(y2, m2 - 1, d2);

          if (date1 && date2 && date1 <= date2) {
            const diffTime = Math.abs(date2 - date1);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            newData.totalDias = diffDays;
          } else {
            newData.totalDias = 0;
          }
        }
      }

      return newData;
    });
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const collectionName = editingType === "gozadas" ? "gozadas" : "pendientes";
      const docRef = doc(db, `vacaciones/${dni}/${collectionName}`, editingItem.id);

      // Campos a actualizar
      const updateData = { ...editForm };
      delete updateData.id;

      // Convertir strings de fecha "YYYY-MM-DD" a Date objects
      if (typeof updateData.fechaInicio === 'string' && updateData.fechaInicio.includes('-')) {
        const [y, m, d] = updateData.fechaInicio.split('-').map(Number);
        updateData.fechaInicio = new Date(y, m - 1, d, 12, 0, 0);
      }
      if (typeof updateData.fechaFin === 'string' && updateData.fechaFin.includes('-')) {
        const [y, m, d] = updateData.fechaFin.split('-').map(Number);
        updateData.fechaFin = new Date(y, m - 1, d, 12, 0, 0);
      }
      if (typeof updateData.fechaVencimiento === 'string' && updateData.fechaVencimiento.includes('-')) {
        const [y, m, d] = updateData.fechaVencimiento.split('-').map(Number);
        updateData.fechaVencimiento = new Date(y, m - 1, d, 12, 0, 0);
      }
      // Pendientes dates
      if (typeof updateData.fechaInicioPeriodo === 'string' && updateData.fechaInicioPeriodo.includes('-')) {
        const [y, m, d] = updateData.fechaInicioPeriodo.split('-').map(Number);
        updateData.fechaInicioPeriodo = new Date(y, m - 1, d, 12, 0, 0);
      }
      if (typeof updateData.fechaFinPeriodo === 'string' && updateData.fechaFinPeriodo.includes('-')) {
        const [y, m, d] = updateData.fechaFinPeriodo.split('-').map(Number);
        updateData.fechaFinPeriodo = new Date(y, m - 1, d, 12, 0, 0);
      }

      await updateDoc(docRef, updateData);

      // AUDITORÍA
      try {
        await logBusinessAction("EDICION_AUSENCIA", "GestionAusencias", {
          dniEditado: dni,
          cambiosRealizados: {
            ausencia: { anterior: "Existente", nuevo: "Editada" },
            tipo: { anterior: null, nuevo: editingType }
          },
          idDocumento: editingItem.id
        }, usuario);
      } catch (e) {
        console.warn("Error al registrar auditoría:", e);
      }

      setShowModal(false);
      handleBuscar();
    } catch (err) {
      console.error("Error al actualizar:", err);
      alert("Error al guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (timestampOrDate) => {
    if (!timestampOrDate) return "";
    let date;
    if (timestampOrDate.seconds) {
      date = new Date(timestampOrDate.seconds * 1000);
    } else if (timestampOrDate instanceof Date) {
      date = timestampOrDate;
    } else {
      // En caso de que ya sea string del input
      return timestampOrDate;
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "-";
    return new Date(timestamp.seconds * 1000).toLocaleDateString("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "America/Lima",
    });
  };

  const getEstadoBadge = (estado) => {
    const variants = {
      Aprobado: "success",
      Pendiente: "warning",
      Rechazado: "danger",
      Gozado: "primary",
    };
    return <Badge bg={variants[estado] || "secondary"}>{estado}</Badge>;
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(45deg, #2c3e50, #3498db)",
              borderBottom: "3px solid #f39c12",
            }}
          >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaCalendarCheck className="me-3" />
                Detalle de Vacaciones
              </h2>
              <p className="mb-0 opacity-75">
                Consulte el historial de vacaciones por DNI
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Formulario de búsqueda */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={6} lg={4}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label className="fw-semibold text-dark">
                        <FaUser className="me-2" />
                        Documento de Identidad
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={dni}
                        onChange={(e) =>
                          setDni(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="Ingrese DNI del colaborador"
                        className="form-control-lg"
                        maxLength={8}
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} lg={3}>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleBuscar}
                      disabled={loading || !dni.trim()}
                      className="w-100"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #3498db, #2980b9)",
                        border: "none",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Buscando...
                        </>
                      ) : (
                        <>
                          <FaSearch className="me-2" />
                          Buscar
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Información del colaborador */}
            {nombre && (
              <Alert
                variant="info"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaUser className="me-3 fs-4" />
                  <div>
                    <h5 className="fw-bold mb-1">Colaborador Encontrado</h5>
                    <p className="mb-0 fs-6">
                      {nombre} - DNI: {dni}
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Error */}
            {error && (
              <Alert
                variant="warning"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaCalendarTimes className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Sin Resultados</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Vacaciones Gozadas */}
            {(gozadas.length > 0 || (nombre && !loading)) && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-success text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaCalendarCheck className="text-white me-3" style={{ fontSize: "1.5rem" }} />
                    <h5 className="mb-0 fw-semibold">Vacaciones Gozadas</h5>
                    <Badge bg="light" text="success" className="ms-auto">
                      {gozadas.length} registro(s)
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-semibold border-0">Periodo</th>
                          <th className="fw-semibold border-0">Estado</th>
                          <th className="fw-semibold border-0">Fecha Inicio</th>
                          <th className="fw-semibold border-0">Fecha Fin</th>
                          <th className="fw-semibold border-0 text-center">
                            Días
                          </th>
                          <th className="fw-semibold border-0 text-center">
                            Documento
                          </th>
                          <th className="fw-semibold border-0 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gozadas.length === 0 ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              <FaCalendarTimes className="me-2" />
                              No hay vacaciones gozadas registradas
                            </td>
                          </tr>
                        ) : (
                          gozadas.map((v, idx) => (
                            <tr key={v.id || idx}>
                              <td className="fw-medium">{v.periodo || "-"}</td>
                              <td>{getEstadoBadge(v.estado)}</td>
                              <td>{formatDate(v.fechaInicio)}</td>
                              <td>{formatDate(v.fechaFin)}</td>
                              <td className="text-center">
                                <Badge bg="primary">{v.totalDias || 0}</Badge>
                              </td>
                              <td className="text-center">
                                {v.pdfUrl ? (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    href={v.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ borderRadius: "8px" }}
                                  >
                                    <FaFilePdf className="me-1" />
                                    Ver PDF
                                  </Button>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEditClick(v, "gozadas")}
                                  title="Editar"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(v.id, "gozadas")}
                                  title="Eliminar"
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                        {/* Footer con Sumatoria */}
                        {gozadas.length > 0 && (
                          <tr className="table-info fw-bold">
                            <td colSpan={4} className="text-end">Total Días Gozados:</td>
                            <td className="text-center">
                              {gozadas.reduce((sum, v) => sum + (Number(v.totalDias) || 0), 0)}
                            </td>
                            <td colSpan={2}></td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Vacaciones Pendientes */}
            {(pendientes.length > 0 || (nombre && !loading)) && (
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-warning text-dark py-3">
                  <div className="d-flex align-items-center">
                    <FaCalendarCheck className="text-black me-3" style={{ fontSize: "1.5rem" }} />
                    <h5 className="mb-0 fw-semibold">
                      Vacaciones Ganadas / Pendientes
                    </h5>
                    <Badge bg="dark" className="ms-auto">
                      {pendientes.length} registro(s)
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-semibold border-0">Periodo</th>
                          <th className="fw-semibold border-0">Estado</th>
                          <th className="fw-semibold border-0 text-center">
                            Días Ganados
                          </th>
                          <th className="fw-semibold border-0">
                            Inicio Periodo
                          </th>
                          <th className="fw-semibold border-0">Fin Periodo</th>
                          <th className="fw-semibold border-0">Vencimiento</th>
                          <th className="fw-semibold border-0 text-center">
                            Días Pendientes
                          </th>
                          <th className="fw-semibold border-0 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientes.length === 0 ? (
                          <tr>
                            <td
                              colSpan={9}
                              className="text-center text-muted py-4"
                            >
                              <FaCalendarTimes className="me-2" />
                              No hay vacaciones pendientes registradas
                            </td>
                          </tr>
                        ) : (
                          pendientes.map((v, idx) => {
                            // Suma los días gozados para el mismo periodo
                            const gozadosPeriodo = gozadas
                              .filter((g) => g.periodo === v.periodo)
                              .reduce(
                                (sum, g) => sum + (Number(g.totalDias) || 0),
                                0
                              );
                            const pendiente =
                              (Number(v.diasGanados) || 0) - gozadosPeriodo;

                            // Calcular estado dinámico
                            let estadoPendiente = "";
                            const vencimiento = v.fechaVencimiento?.seconds
                              ? new Date(v.fechaVencimiento.seconds * 1000)
                              : null;
                            const hoy = new Date();

                            if (pendiente <= 0) {
                              estadoPendiente = "Finalizado";
                            } else if (vencimiento && hoy > vencimiento) {
                              estadoPendiente = "Vencido";
                            } else {
                              estadoPendiente = "Vigente";
                            }

                            return (
                              <tr key={v.id || idx}>
                                <td className="fw-medium">
                                  {v.periodo || "-"}
                                </td>
                                <td>
                                  <Badge bg={
                                    estadoPendiente === "Finalizado"
                                      ? "primary"
                                      : estadoPendiente === "Vencido"
                                        ? "danger"
                                        : "success"
                                  }>
                                    {estadoPendiente}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Badge bg="info">{v.diasGanados || 0}</Badge>
                                </td>
                                <td>{formatDate(v.fechaInicioPeriodo)}</td>
                                <td>{formatDate(v.fechaFinPeriodo)}</td>
                                <td>{formatDate(v.fechaVencimiento)}</td>
                                <td className="text-center">
                                  <Badge
                                    bg={pendiente > 0 ? "warning" : "danger"}
                                  >
                                    {pendiente}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEditClick(v, "pendientes")}
                                    title="Editar"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDelete(v.id, "pendientes")}
                                    title="Eliminar"
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })

                        )}
                        {/* Footer con Sumatoria */}
                        {pendientes.length > 0 && (
                          <tr className="table-warning fw-bold text-dark">
                            <td colSpan={6} className="text-end">Total Días Pendientes:</td>
                            <td className="text-center">
                              {pendientes.reduce((sum, v) => {
                                const gozadosPeriodo = gozadas
                                  .filter((g) => g.periodo === v.periodo)
                                  .reduce(
                                    (s, g) => s + (Number(g.totalDias) || 0),
                                    0
                                  );
                                const pendiente =
                                  (Number(v.diasGanados) || 0) - gozadosPeriodo;
                                return sum + (pendiente > 0 ? pendiente : 0);
                              }, 0)}
                            </td>
                            <td></td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Cuadro de Riesgos Legales para la empresa */}
            {(pendientes.length > 0 || gozadas.length > 0) && (
              <Card
                className="shadow-sm border-0 mb-4 mt-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-danger text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaBalanceScale className="text-white me-3" style={{ fontSize: "1.5rem" }} />
                    <h5 className="mb-0 fw-semibold">
                      Cuadro de Riesgos Legales para la empresa
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table responsive hover className="mb-0 table-horizontal-lines">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-bold">Periodo</th>
                          <th className="fw-semibold border-0">Estado actual</th>
                          <th className="fw-semibold border-0">Riesgo legal si no se otorgan</th>
                          <th className="fw-semibold border-0">Implicancias económicas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientes.map((v, idx) => {
                          const gozadosPeriodo = gozadas
                            .filter((g) => g.periodo === v.periodo)
                            .reduce(
                              (sum, g) => sum + (Number(g.totalDias) || 0),
                              0
                            );
                          const pendiente =
                            (Number(v.diasGanados) || 0) - gozadosPeriodo;

                          let estadoLegal = "";
                          let riesgoLegal = "";
                          let implicancia = "";

                          const vencimiento = v.fechaVencimiento?.seconds
                            ? new Date(v.fechaVencimiento.seconds * 1000)
                            : null;
                          const hoy = new Date();

                          if (pendiente <= 0) {
                            estadoLegal = "Gozado";
                            riesgoLegal = "Sin riesgo legal";
                            implicancia = "Sin implicancia económica";
                          } else if (vencimiento && hoy > vencimiento) {
                            estadoLegal = `Vencido (debió gozarse hasta el ${vencimiento.toLocaleDateString(
                              "es-PE"
                            )})`;
                            riesgoLegal =
                              "Indemnización vacacional: la empresa debe pagar 1 remuneración adicional (además de seguir debiendo el descanso)";
                            implicancia = "1 sueldo extra + descanso pendiente";
                          } else if (vencimiento && hoy <= vencimiento) {
                            estadoLegal = `En curso (vence el ${vencimiento.toLocaleDateString(
                              "es-PE"
                            )})`;
                            riesgoLegal =
                              "Si no se otorga antes del vencimiento, genera indemnización vacacional";
                            implicancia = `Riesgo de 1 sueldo extra más descanso pendiente si no se otorga antes de ${vencimiento.toLocaleDateString(
                              "es-PE"
                            )}`;
                          } else {
                            estadoLegal = "Sin información de vencimiento";
                            riesgoLegal = "-";
                            implicancia = "-";
                          }

                          return (
                            <tr key={v.id || idx}>
                              <td className="fw-bold">{v.periodo}</td>
                              <td>{estadoLegal}</td>
                              <td>{riesgoLegal}</td>
                              <td>{implicancia}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Modal de Edición */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                  <FaEdit className="me-2" />
                  Editar Registro de Vacaciones
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Periodo</Form.Label>
                    <Form.Control
                      type="text"
                      name="periodo"
                      value={editForm.periodo || ""}
                      onChange={handleEditChange}
                    />
                  </Form.Group>

                  {editingType === "gozadas" && (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Fecha Inicio</Form.Label>
                            <Form.Control
                              type="date"
                              name="fechaInicio"
                              value={formatDateForInput(editForm.fechaInicio)}
                              onChange={handleEditChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Fecha Fin</Form.Label>
                            <Form.Control
                              type="date"
                              name="fechaFin"
                              value={formatDateForInput(editForm.fechaFin)}
                              onChange={handleEditChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Total Días (Calculado Automáticamente)</Form.Label>
                        <Form.Control
                          type="number"
                          name="totalDias"
                          value={editForm.totalDias || ""}
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </>
                  )}

                  {editingType === "pendientes" && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Días Ganados</Form.Label>
                        <Form.Control
                          type="number"
                          name="diasGanados"
                          value={editForm.diasGanados || ""}
                          onChange={handleEditChange}
                        />
                      </Form.Group>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Inicio Periodo</Form.Label>
                            <Form.Control
                              type="date"
                              name="fechaInicioPeriodo"
                              value={formatDateForInput(editForm.fechaInicioPeriodo)}
                              onChange={handleEditChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Fin Periodo</Form.Label>
                            <Form.Control
                              type="date"
                              name="fechaFinPeriodo"
                              value={formatDateForInput(editForm.fechaFinPeriodo)}
                              onChange={handleEditChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>Vencimiento Actual</Form.Label>
                        <Form.Control
                          type="date"
                          name="fechaVencimiento"
                          value={formatDateForInput(editForm.fechaVencimiento)}
                          onChange={handleEditChange}
                        />
                      </Form.Group>
                    </>
                  )}
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  <FaTimes className="me-2" />
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default VacacionesDetalle;
